import './Holidays.scss';

import { faStore } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { Button } from 'primereact/button';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import useAxiosHook from '../../../hooks/useAxiosHook';
import useHaveValuesChanged from '../../../hooks/useHaveValuesChanged';
import usePageTitle from '../../../hooks/usePageTitle';
import useRouteDialog from '../../../hooks/useRouteDialog';
import useSearchQueryParam from '../../../hooks/useSearchQueryParam';
import {
  RoutePaths,
  constructIdRoute,
} from '../../../utils/constants/routePaths';
import { sequential } from '../../../utils/helpers/functions';
import { queryString } from '../../../utils/helpers/http';
import { httpQueryObject } from '../../../utils/helpers/misc';
import { getSearchQueryParam } from '../../../utils/helpers/searchQuery';
import DialogSpinner from '../../Dialogs/DialogSpinner/DialogSpinner';
import Flex from '../../layout/flex/Flex';
import MainContent from '../../layout/flex/MainContent';
import HeaderPages from '../Components/HeaderPages/HeaderPages';
import YearPicker from '../Components/YearPicker/YearPicker';
import CreateDialog from './Dialogs/CreateHoliday';
import { HolidaysType } from './Holiday.functions';
import SingleMonth from './SingleMonth';

function Holidays() {
  const { t } = useTranslation();
  const location = useLocation();
  usePageTitle(t('Holidays'));
  const months = Array.from({ length: 12 }, (_, i) => i);
  const [year, setYear] = useState<string>(
    getSearchQueryParam(location.search, 'year') ?? dayjs().year().toString()
  );
  const [idHoliday, setIdHoliday] = useState<number | undefined>();

  const httpFiltersObj = useMemo(() => {
    return {
      year: year,
      page: 1,
      limit: 50,
    };
  }, [year]);

  useSearchQueryParam('year', year);

  const { data, isLoading, reload } = useAxiosHook<HolidaysType | undefined>(
    {
      url: '/holidays' + queryString(httpQueryObject(httpFiltersObj)),
    },
    { skipWhen: !year, skipCache: true }
  );
  console.log('data ', data);

  const {
    show: showCreateDialog,
    hide: hideCreateDialog,
    isVisible: isCreateDialogVisible,
  } = useRouteDialog(RoutePaths.Holidays, RoutePaths.HolidaysCreate, true);

  const {
    show: showEditDialog,
    hide: hideEditDialog,
    isVisible: isEditDialogVisible,
  } = useRouteDialog(
    RoutePaths.Holidays,
    constructIdRoute(RoutePaths.HolidaysEdit, idHoliday),
    true
  );

  function handleCreateHoliday() {
    setIdHoliday(undefined);
    showCreateDialog();
  }

  useEffect(() => {
    if (idHoliday !== undefined) {
      showEditDialog();
    }
  }, [idHoliday, showEditDialog]);

  return (
    <div className="page holidays">
      <HeaderPages title={t('Holidays')} icon={faStore}>
        <Button
          type="button"
          icon="fas fa-add"
          label={t('Add holiday')}
          disabled={isLoading}
          onClick={handleCreateHoliday}
          className="p-button-primary"
        />
      </HeaderPages>

      <CreateDialog
        isEditDialog={isEditDialogVisible}
        isShown={isCreateDialogVisible || isEditDialogVisible}
        onHide={() => sequential(hideEditDialog, hideCreateDialog)}
        idHoliday={idHoliday}
        reloadCollection={reload}
      />

      <Flex direction="column">
        <div className="sidebar_filters">
          <YearPicker value={year} onChange={(year) => setYear(String(year))} />
        </div>
        <MainContent>
          {isLoading && <DialogSpinner />}
          {!isLoading && data && (
            <div className="year-calendar">
              {months.map((month) => (
                <div key={month} className="year-calendar-month">
                  <SingleMonth
                    month={month}
                    year={year}
                    holidays={data?.data}
                    setIdHoliday={setIdHoliday}
                  />
                </div>
              ))}
            </div>
          )}
        </MainContent>
      </Flex>
    </div>
  );
}
export default Holidays;
